import { Button } from '@svs/ui/react/button';
import { type ReactNode } from 'react';

import { cn } from '../utils/entry.js';

type ShareAlternativesProps = {
  isDisabled: boolean;
  shareStatus: ShareStatus;
  setShareStatus: (status: ShareStatus) => void;
  url: string;
  className?: string;
  text?: string;
  variant?: React.ComponentProps<typeof Button>['variant'];
  onClick?: React.ComponentProps<typeof Button>['onClick'];
};

type ShareStatus = 'None' | 'CopiedToClipboard' | 'Shared' | 'Text';

const copyTextToClipboard = async (text: string): Promise<ShareStatus> => {
  await navigator.clipboard.writeText(text);
  return 'CopiedToClipboard';
};

const share = async (url: string): Promise<ShareStatus> => {
  const shareConfig = {
    title: 'Spela hos Svenska Spel S&C',
    text: `Spela hos Svenska Spel S&C`,
  };
  const canShare = navigator.canShare(shareConfig);
  if (canShare) {
    try {
      await navigator.share({
        ...shareConfig,
        url,
      });
      return 'Shared';
    } catch {
      return 'None';
    }
  }
  return copyTextToClipboard(url);
};

/**
 * This component renders a button for sharing the current coupon.
 *
 * @param root0 the input props
 * @param root0.isDisabled the status of the current coupon
 * @param root0.shareStatus the current share status
 * @param root0.setShareStatus the function to set the share status
 * @param root0.url the url to share
 * @param root0.className additional class names for the button
 * @param root0.text the text to display on the button, defaults to "Dela kupong"
 * @param root0.variant the variant of the button, defaults to "outline"
 * @param root0.onClick the onClick handler for the button
 * @returns the share coupon button
 */
export default function ShareButtonAlternatives({
  isDisabled,
  shareStatus,
  setShareStatus,
  url,
  className,
  text,
  variant,
  onClick,
}: ShareAlternativesProps): ReactNode {
  const handleShare = async (): Promise<void> => {
    let newShareStatus: ShareStatus;
    try {
      newShareStatus = await share(url);
    } catch {
      newShareStatus = 'Text';
    }
    setShareStatus(newShareStatus);
  };

  if (shareStatus === 'Text') {
    return (
      <div className="mt-[12px] grid border-black">
        <input
          aria-label="Dela din kupong"
          className="rounded-xs"
          name="shareInput"
          readOnly
          ref={(node) => {
            if (!node) {
              return;
            }
            node.focus();
            node.select();
          }}
          type="text"
          value={url}
        />
        <div>Kopiera länken och dela den med dina vänner</div>
      </div>
    );
  }

  return (
    <Button
      className={cn('lg:h-[40px] lg:w-full', className)}
      data-testid="overviewShareButton"
      disabled={isDisabled}
      onClick={(e) => {
        void handleShare();
        if (onClick) {
          onClick(e);
        }
      }}
      variant={variant ?? 'outline'}
    >
      {text ?? 'Dela kupong'}
    </Button>
  );
}
